/*  

<summary>
  Functional Component: CategoryList
  Developer: Mohammad Saquib Khan
  Created Date: 06-Jan-2025
  Description: Renders a staff management interface with features for viewing, adding, editing, deleting, and filtering staff. Includes pagination, popups for detailed views, and manual refresh and invalid staff toggles.
</summary>
<returns>JSX for the Staff Management interface</returns>
*/

import { observer } from "mobx-react";
import moment from "moment";
import "rc-tree/assets/index.css";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";
import bookingStatusEnum from "../../../constants/enums/booking-status-enum";
import userSettingEnum from "../../../constants/enums/user-setting-enum";
import { useStore } from "../../../contexts/store-provider";
import {
  getcustIdFromToken
} from "../../../helpers/localstorage-helper";
import IPageInfo, { IPageLimitInfo } from "../../../models/ICommon";
import Pagination from "../../../shared-components/Grid/Pagination";
import { formatMessage } from "../../../translations/format-message";
import "./reservation-list.css";

import noImg from "../../../assets/Images/no_image_available.jpg";
import noMessageIcon from "../../../assets/Images/svg/018-empty.svg";
import {
  formatNumberWithCommas,
  getOptionNameFromServiceName,
} from "../../../constants/constants";
import { serviceTypeEnum } from "../../../constants/enums/service-type-enum";
import CustomDatePicker from "../../../shared-components/date-picker/custome-date-picker";
import CustomerTopNavbar from "../../../shared-components/navbar/customer-top-navbar";
import RoutesConstants from "../../../shared-components/routes/routes-constants";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";

const ReservationList: React.FC = (props: any) => {
  const history = useHistory();
  const {
    categoryManagementStore,
    preferencesStore,
    bookingManagementStore,
    masterManagementStore,
  } = useStore();
  const [openPanel, setOpenPanel] = useState(false);
  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const {
    allReservationList,
    reservationList,
    GetReservationListFilterService,
    resetAppointmentState,
    bookingStatus,
    setBookingStatus,
    getBookingStatus,
    allPastReservationList,
    allCurrentReservationList,
    getReservationListState
  } = bookingManagementStore;
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [startDate, setStartDate] = React.useState<string | null>(
    moment().format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = React.useState<string | null>(
    moment().add(2, "months").format("YYYY-MM-DD")
  );

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const validateDates = (start: any, end: any) => {
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      
      // Reset time to compare only dates
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      if (startDate > endDate) {
        return false;
      }
    }
    return true;
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: string) => {
    setEndDate(date);
  };

  useEffect(() => {
    // Only call API if dates are valid
    if (startDate && endDate && validateDates(startDate, endDate)) {
      callGetReservationListService();
    }
    else{
      if(endDate && startDate)
        toast.error(formatMessage("end_date_must_be_same_or_after_start_date"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBookingStatus, startDate, endDate]);

  const handleBookingStatus = (event: any) => {
    event.target.value = event.target.value;
    setBookingStatus(Number(event.target.value));
  };

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (evt: any) => {
    let pages: number;
    setPageInfo({
      ...pageInfo,
      currentPage: 1,
      totalPages: pageInfo.totalPages,
      isPagerChange: false,
    });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  /**
   * This function calls the "getAllGroup" staff funtion that get configuration list from API
   */
  const callGetReservationListService = () => {
    GetReservationListFilterService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      startDate,
      endDate,
      bookingStatus,
      getcustIdFromToken()
    );
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "GroupList" of the useEffect changed.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      totalPages: reservationList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allReservationList]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageInfo.currentPage" of the useEffect changed.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      callGetReservationListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageLimitInfo.pageLimit" of the useEffect changed.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetReservationListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  const openReservation = (data: any) => {
    resetAppointmentState();
    history.push({
      pathname: RoutesConstants.ReservationDetails,
      state: JSON.parse(JSON.stringify(data)),
    });
  };

  const openBooking = (data: any) => {
    resetAppointmentState();

    // Filter out items where IsValid is false
    const filteredData = {
      ...data,
      ServiceRequestedVMss: data.ServiceRequestedVMss.filter(
        (service: any) => service.IsValid !== false
      ),
    };

    if (filteredData.ServiceRequestedVMss.length > 0) {
      history.push({
        pathname: `/Store/${data.Store.StoreCode}/reservation`,
        state: JSON.parse(JSON.stringify(filteredData)),
      });
    } else {
      toast.error(formatMessage("no_valid_service_available_for_booking"));
    }
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetReservationListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);
  return (
    <React.Fragment>

      <CustomerTopNavbar />

      <ol className="breadcrumb ps-2 pt-3">
        <li className="breadcrumb-item">
          <svg
            className="me-1"
            fill="currentColor"
            version="1.1"
            id="fi_25694"
            x="0px"
            y="0px"
            width="15"
            height="15"
            viewBox="0 0 460.298 460.297"
          >
            <g>
              <g>
                <path
                  d="M230.149,120.939L65.986,256.274c0,0.191-0.048,0.472-0.144,0.855c-0.094,0.38-0.144,0.656-0.144,0.852v137.041
                  c0,4.948,1.809,9.236,5.426,12.847c3.616,3.613,7.898,5.431,12.847,5.431h109.63V303.664h73.097v109.64h109.629
                  c4.948,0,9.236-1.814,12.847-5.435c3.617-3.607,5.432-7.898,5.432-12.847V257.981c0-0.76-0.104-1.334-0.288-1.707L230.149,120.939
                  z"
                ></path>
                <path
                  d="M457.122,225.438L394.6,173.476V56.989c0-2.663-0.856-4.853-2.574-6.567c-1.704-1.712-3.894-2.568-6.563-2.568h-54.816
                  c-2.666,0-4.855,0.856-6.57,2.568c-1.711,1.714-2.566,3.905-2.566,6.567v55.673l-69.662-58.245
                  c-6.084-4.949-13.318-7.423-21.694-7.423c-8.375,0-15.608,2.474-21.698,7.423L3.172,225.438c-1.903,1.52-2.946,3.566-3.14,6.136
                  c-0.193,2.568,0.472,4.811,1.997,6.713l17.701,21.128c1.525,1.712,3.521,2.759,5.996,3.142c2.285,0.192,4.57-0.476,6.855-1.998
                  L230.149,95.817l197.57,164.741c1.526,1.328,3.521,1.991,5.996,1.991h0.858c2.471-0.376,4.463-1.43,5.996-3.138l17.703-21.125
                  c1.522-1.906,2.189-4.145,1.991-6.716C460.068,229.007,459.021,226.961,457.122,225.438z"
                ></path>
              </g>
            </g>
          </svg>
          <FormattedMessage id="Home" />
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          <FormattedMessage id="reservation" />
        </li>
      </ol>
      <div className="container-fluid mt-4 p-0">
        { getReservationListState.inProgress &&
          <FormLoader loading={getReservationListState.inProgress} />
        }
        <div className="row justify-content-between align-items-end">
          <div className="col-xxl-4 col-xl-5 col-lg-6 col-md-7 col-sm-8  col-12 mb-3">
            <div className="row reservation_date_row">
              <div className="col-6">
                <label className="">
                  <FormattedMessage id="start_date" />
                </label>
                <CustomDatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                />
              </div>
              <div className="col-6">
                <label className="">
                  <FormattedMessage id="end_date" />
                </label>
                <CustomDatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                />
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-4 col-12 mb-3">
            <select
              id="options"
              value={getBookingStatus}
              className="form-control"
              onChange={handleBookingStatus}
            >
              <option value="-1">{formatMessage("all_reservations")}</option>
              <option value="0">{formatMessage("booked")}</option>
              <option value="1">{formatMessage("cancelled")}</option>
              <option value="2">{formatMessage("completed")}</option>
            </select>
          </div>
        </div>
        <div className={`${openPanel ? "panelOpen" : ""}`}>
          {
            validateDates(startDate, endDate) ?
            <>
              {allReservationList?.length > 0 ? (
                <>
                  {allCurrentReservationList.length > 0 && (
                    <h1>{formatMessage("current_reservation")}</h1>
                  )}
                  {allCurrentReservationList?.map((reservations: any) => {
                    let charges = 0;
                    return (
                      <div className={`${
                          reservations?.AppointmentStatus ===
                            bookingStatusEnum.Cancelled
                            ? "bg-danger-light border-danger"
                            : ""
                        } reservation_list_wrapper mb-3`}>
                        <div className="row g-0">
                          <div className="col-xl-3 col-lg-4 col-sm-4 col-12">
                            <img
                              src={`${
                                reservations?.Store?.Image
                                  ? reservations?.Store?.Image
                                  : noImg
                              }`}
                              className="img-fluid reservation_img"
                              alt=""
                            />
                          </div>
                          <div className="col-xl-9 col-lg-8 col-sm-8 col-12 p-3 ">
                            <div className="row align-items-center">
                              <div className="col-12 col-sm-8">
                                <h3 className="mb-0  text-wrap">
                                  {reservations?.Store?.StoreName}
                                </h3>
                                <p className="text-muted">
                                  {formatMessage("contact_number")}:{" "}
                                  {reservations?.Store?.MobileNumber}
                                </p>
                              </div>
                              <div className="col-12 col-sm-4 text-sm-end">
                                <span
                                  className={`d-inline-block my-2 my-sm-0 py-2 px-3 text-white ${
                                    reservations?.AppointmentStatus == 0
                                      ? "bg-success"
                                      : reservations?.AppointmentStatus == 1
                                      ? "bg-danger"
                                      : "completed-customer-reservation"
                                  }`}
                                >
                                  <FormattedMessage
                                    id={`status_${
                                      bookingStatusEnum[
                                        reservations?.AppointmentStatus
                                      ]
                                    }`}
                                  />
                                </span>
                              </div>
                            </div>

                            <div className="card mb-2 mt-1">
                              <div className="row g-0">
                                <div className="col-xl-3 col-lg-4 col-sm-4 col-12 bg-light py-2 px-3 px-xl-5 border-end border-bottom">
                                  {formatMessage("menu")}
                                </div>
                                <div className="col-xl-9 col-lg-8 col-sm-8 col-12 py-2 px-3 border-bottom">
                                  <div className="row align-items-center text-wrap">
                                    <div className="col-12 col-sm-8 col-lg-9">
                                      {reservations?.ServiceRequestedVMss?.map(
                                        (services: any) => {
                                          charges = Number(reservations.TotalAmount);
                                          let serviceName =
                                            services.ServiceType ===
                                            serviceTypeEnum.Option
                                              ? getOptionNameFromServiceName(
                                                  services?.ServiceName
                                                )
                                              : services?.ServiceName;
                                          return (
                                            // <div className="row">
                                            <p className="m-0">{serviceName}
                                            {
                                              services.ServiceType === serviceTypeEnum.Option &&
                                              <span className="badge bg-primary rounded ms-2">
                                                <FormattedMessage id="option" />
                                              </span>
                                            }
                                            </p>
                                          );
                                        }
                                      )}
                                    </div>
                                    <div className="col-lg-3 col-12 col-sm-4 text-sm-end">
                                      <span className="fw-bold">
                                        <i className="bi bi-currency-yen me-1"></i>
                                        {formatNumberWithCommas(
                                          Number(charges)
                                        )}{" "}
                                      </span>
                                    </div>
                                    {/* <p className="m-0">Advance Nail Cure</p> */}
                                  </div>
                                </div>
                              </div>
                              <div className="row g-0">
                                <div className="col-xl-3 col-lg-4 col-sm-4 col-12 bg-light py-2 px-3 px-xl-5 border-end border-bottom">
                                  {formatMessage("staff")}
                                </div>
                                <div className="col-xl-9 col-lg-8 col-sm-8 col-12 py-2 px-3 border-bottom">
                                  {
                                    reservations?.StaffPreference == 1 ? formatMessage("male_hope") : reservations.StaffPreference == 2 ? formatMessage("women_hope") : reservations?.StaffPreference == 0 ? formatMessage("no_nomination") : 
                                    <>
                                      {reservations?.StaffName
                                        ? reservations?.StaffName
                                        : formatMessage("specific")
                                      }
                                    </>
                                  }
                                  {" "} - <i className="bi bi-currency-yen me-1"></i>
                                      {formatNumberWithCommas(
                                        reservations?.NominationFee
                                      )}
                                </div>
                              </div>
                              <div className="row g-0">
                                <div className="col-xl-3 col-lg-4 col-sm-4 col-12 bg-light py-2 px-3 px-xl-5 border-end">
                                  {/* Date and Time */}
                                  {formatMessage("date_and_time")}
                                </div>
                                <div className="col-xl-9 col-lg-8 col-sm-7 col-12 py-2 px-3">
                                  {/* {formatMessage(
                                    {moment(reservations.Date).format(
                                      "YYYY-MM-DD (dddd)"
                                    )}
                                  )} */}
                                  {moment(reservations.Date).format("YYYY-MM-DD")} (
                                    {formatMessage(
                                      moment(reservations.Date).format("ddd")
                                    )}
                                  )

                                  {" "}
                                  {moment(reservations.Time, "HH:mm:ss").format(
                                    "HH:mm"
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row align-items-center">
                              <div className="col-12 col-sm-8">
                                <p className="text-muted text-wrap">
                                  {reservations?.Store?.Address}
                                </p>
                                <a
                                  href={reservations?.Store?.Url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary"
                                >
                                  <svg
                                    id="fi_2838856"
                                    fill="currentColor"
                                    className="me-1"
                                    enableBackground="new 0 0 512 512"
                                    height="16"
                                    viewBox="0 0 512 512"
                                    width="16"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <g>
                                      <path d="m76 180c0 88.916 67.881 185.384 127.778 268.748 14.238 19.805 27.876 38.774 39.741 56.572 2.783 4.175 7.471 6.68 12.48 6.68s9.697-2.505 12.48-6.68c11.865-17.798 25.503-36.768 39.741-56.572 59.899-83.364 127.78-179.832 127.78-268.748 0-99.258-80.742-180-180-180s-180 80.742-180 180zm270 0c0 49.629-40.371 90-90 90s-90-40.371-90-90 40.371-90 90-90 90 40.371 90 90z"></path>
                                      <circle cx="256" cy="180" r="60"></circle>
                                    </g>
                                  </svg>
                                  {reservations?.Store?.Url
                                    ? formatMessage("map")
                                    : formatMessage("no_location_available")}
                                </a>
                              </div>
                              {(reservations?.AppointmentStatus ===
                                bookingStatusEnum.Cancelled ||
                                reservations?.AppointmentStatus ===
                                  bookingStatusEnum.Complete) && (
                                <div className="col-12 col-sm-4 text-sm-end">
                                  <button
                                    className={`btn btn-sm px-4 bg-dark text-white`}
                                    onClick={() => openBooking(reservations)}
                                  >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    <FormattedMessage
                                      id={
                                        reservations?.AppointmentStatus ==
                                        bookingStatusEnum.Complete
                                          ? "book_same_content"
                                          : "book_different_content"
                                      }
                                    />
                                  </button>
                                </div>
                              )}
                              <div
                                className={`${
                                  reservations?.AppointmentStatus ===
                                    bookingStatusEnum.Cancelled ||
                                  reservations?.AppointmentStatus ===
                                    bookingStatusEnum.Complete
                                    ? "col-12 col-sm-2"
                                    : "col-12 col-sm-4"
                                } text-sm-end mt-2 mt-sm-0`}
                              >
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => openReservation(reservations)}
                                >
                                  <svg
                                    className="me-2"
                                    version="1.1"
                                    width="16px"
                                    height="16px"
                                    id="fi_709612"
                                    x="0px"
                                    y="0px"
                                    viewBox="0 0 511.999 511.999"
                                    fill="currentColor"
                                  >
                                    <g>
                                      <g>
                                        <path
                                          d="M508.745,246.041c-4.574-6.257-113.557-153.206-252.748-153.206S7.818,239.784,3.249,246.035
              c-4.332,5.936-4.332,13.987,0,19.923c4.569,6.257,113.557,153.206,252.748,153.206s248.174-146.95,252.748-153.201
              C513.083,260.028,513.083,251.971,508.745,246.041z M255.997,385.406c-102.529,0-191.33-97.533-217.617-129.418
              c26.253-31.913,114.868-129.395,217.617-129.395c102.524,0,191.319,97.516,217.617,129.418
              C447.361,287.923,358.746,385.406,255.997,385.406z"
                                        ></path>
                                      </g>
                                    </g>
                                    <g>
                                      <g>
                                        <path
                                          d="M255.997,154.725c-55.842,0-101.275,45.433-101.275,101.275s45.433,101.275,101.275,101.275
              s101.275-45.433,101.275-101.275S311.839,154.725,255.997,154.725z M255.997,323.516c-37.23,0-67.516-30.287-67.516-67.516
              s30.287-67.516,67.516-67.516s67.516,30.287,67.516,67.516S293.227,323.516,255.997,323.516z"
                                        ></path>
                                      </g>
                                    </g>
                                  </svg>
                                  <FormattedMessage id="see_details" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {allPastReservationList.length > 0 && (
                    <h1>{formatMessage("past_reservation")}</h1>
                  )}
                  {allPastReservationList?.map((reservations: any) => {
                                //  console.log(reservations);
                    let charges = 0;
                    return (
                      <div className={`${reservations?.AppointmentStatus === bookingStatusEnum.Reserved
                          ? "bg-success"
                          : reservations?.AppointmentStatus == bookingStatusEnum.Cancelled
                          ? "bg-danger-light border-danger"
                          : "bg-light border"} 
                          reservation_list_wrapper mb-3`}>
                        <div className="row g-0">
                          <div className="col-xl-3 col-lg-4 col-sm-4 col-12">
                            <img
                              src={`${
                                reservations?.Store?.Image
                                  ? reservations?.Store?.Image
                                  : noImg
                              }`}
                              className="img-fluid reservation_img"
                              alt=""
                            />
                          </div>
                          <div className="col-xl-9 col-lg-8 col-sm-8 col-12 p-3 ">
                            <div className="row g-1 align-items-center">
                              <div className="col-8">
                                <h3 className="mb-0  text-wrap">
                                  {reservations?.Store?.StoreName}
                                </h3>
                                <p className="text-muted">
                                  {formatMessage("contact_number")}:{" "}
                                  {reservations?.Store?.MobileNumber}
                                </p>
                              </div>
                              <div className="col-4 text-end">
                                <small
                                  className={`py-sm-2 px-sm-3 py-1 px-1 text-white ${
                                    reservations?.AppointmentStatus == 0
                                      ? "bg-success"
                                      : reservations?.AppointmentStatus == 1
                                      ? "bg-danger"
                                      : "completed-customer-reservation"
                                  }`}
                                >
                                  <FormattedMessage
                                    id={`status_${
                                      bookingStatusEnum[
                                        reservations?.AppointmentStatus
                                      ]
                                    }`}
                                  />
                                </small>
                              </div>
                            </div>

                            <div className="card mb-2 mt-1">
                              <div className="row g-0">
                                <div className="col-xl-3 col-lg-4 col-sm-4 col-12 bg-light py-2 px-3 px-lg-5 border-end border-bottom">
                                  {formatMessage("menu")}
                                </div>
                                <div className="col-xl-9 col-lg-8 col-sm-8 col-12 py-2 px-3 border-bottom">
                                  <div className="row align-items-center">
                                    <div className="col-8 col-lg-9">
                                      {reservations?.ServiceRequestedVMss?.map(
                                        (services: any) => {
                                          charges += Number(services.Charges);
                                          let serviceName =
                                            services.ServiceType ===
                                            serviceTypeEnum.Option
                                              ? getOptionNameFromServiceName(
                                                  services?.ServiceName
                                                )
                                              : services?.ServiceName;
                                          return (
                                            // <div className="row">
                                            <p className="m-0">{serviceName}
                                            {
                                              services.ServiceType === serviceTypeEnum.Option &&
                                              <span className="badge bg-primary rounded ms-2">
                                                <FormattedMessage id="option" />
                                              </span>
                                            }
                                            </p>
                                          );
                                        }
                                      )}
                                    </div>
                                    <div className="col-lg-3 col-4 text-end">
                                      <span className="fw-bold">
                                        <i className="bi bi-currency-yen me-1"></i>
                                        {formatNumberWithCommas(
                                          Number(charges) +
                                            Number(reservations?.NominationFee)
                                        )}{" "}
                                      </span>
                                    </div>
                                    {/* <p className="m-0">Advance Nail Cure</p> */}
                                  </div>
                                </div>
                              </div>
                              <div className="row g-0">
                                <div className="col-xl-3 col-lg-4 col-sm-4 col-12 bg-light py-2 px-3 px-lg-5 border-end border-bottom">
                                  {formatMessage("staff")}
                                </div>
                                <div className="col-xl-9 col-lg-8 col-sm-8 col-12 py-2 px-3 border-bottom">
                                  {reservations?.StaffName
                                    ? reservations?.StaffName
                                    : formatMessage("no_staff_selected")}{" "}
                                  - <i className="bi bi-currency-yen me-1"></i>
                                  {formatNumberWithCommas(
                                    reservations?.NominationFee
                                  )}
                                </div>
                              </div>
                              <div className="row g-0">
                                <div className="col-xl-3 col-lg-4 col-sm-4 col-12 bg-light py-2 px-3 px-lg-5 border-end">
                                  {/* Date and Time */}
                                  {formatMessage("date_and_time")}
                                </div>
                                <div className="col-xl-9 col-lg-8 col-sm-7 col-12 py-2 px-3">
                                  {moment(reservations.Date).format("YYYY-MM-DD")} (
                                    {formatMessage(
                                      moment(reservations.Date).format("ddd")
                                    )}
                                  )
                                  {" "}
                                  {moment(reservations.Time, "HH:mm:ss").format(
                                    "HH:mm"
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row align-items-center">
                              <div className="col-lg-6 col-12">
                                <p className="text-muted text-wrap">
                                  {reservations?.Store?.Address}
                                </p>
                                <a
                                  href={reservations?.Store?.Url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary"
                                >
                                  <svg
                                    id="fi_2838856"
                                    fill="currentColor"
                                    className="me-1"
                                    enableBackground="new 0 0 512 512"
                                    height="16"
                                    viewBox="0 0 512 512"
                                    width="16"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <g>
                                      <path d="m76 180c0 88.916 67.881 185.384 127.778 268.748 14.238 19.805 27.876 38.774 39.741 56.572 2.783 4.175 7.471 6.68 12.48 6.68s9.697-2.505 12.48-6.68c11.865-17.798 25.503-36.768 39.741-56.572 59.899-83.364 127.78-179.832 127.78-268.748 0-99.258-80.742-180-180-180s-180 80.742-180 180zm270 0c0 49.629-40.371 90-90 90s-90-40.371-90-90 40.371-90 90-90 90 40.371 90 90z"></path>
                                      <circle cx="256" cy="180" r="60"></circle>
                                    </g>
                                  </svg>
                                  {reservations?.Store?.Url
                                    ? formatMessage("map")
                                    : formatMessage("no_location_available")}
                                </a>
                              </div>
                              {(reservations?.AppointmentStatus ===
                                bookingStatusEnum.Cancelled ||
                                reservations?.AppointmentStatus ===
                                  bookingStatusEnum.Complete) && (
                                <div className="col-lg-4 col-6 text-lg-end">
                                  <button
                                    className={`btn btn-sm bg-dark text-white`}
                                    onClick={() => openBooking(reservations)}
                                  >
                                    <FormattedMessage
                                      id={
                                        reservations?.AppointmentStatus ==
                                        bookingStatusEnum.Complete
                                          ? "book_same_content"
                                          : "book_different_content"
                                      }
                                    />
                                  </button>
                                </div>
                              )}
                              <div
                                className={`${
                                  reservations?.AppointmentStatus ===
                                    bookingStatusEnum.Cancelled ||
                                  reservations?.AppointmentStatus ===
                                    bookingStatusEnum.Complete
                                    ? "col-lg-2 col-6"
                                    : "col-lg-6 col-12"
                                } text-end`}
                              >
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => openReservation(reservations)}
                                >
                                  <svg
                                    className="me-2"
                                    version="1.1"
                                    width="16px"
                                    height="16px"
                                    id="fi_709612"
                                    x="0px"
                                    y="0px"
                                    viewBox="0 0 511.999 511.999"
                                    fill="currentColor"
                                  >
                                    <g>
                                      <g>
                                        <path
                                          d="M508.745,246.041c-4.574-6.257-113.557-153.206-252.748-153.206S7.818,239.784,3.249,246.035
              c-4.332,5.936-4.332,13.987,0,19.923c4.569,6.257,113.557,153.206,252.748,153.206s248.174-146.95,252.748-153.201
              C513.083,260.028,513.083,251.971,508.745,246.041z M255.997,385.406c-102.529,0-191.33-97.533-217.617-129.418
              c26.253-31.913,114.868-129.395,217.617-129.395c102.524,0,191.319,97.516,217.617,129.418
              C447.361,287.923,358.746,385.406,255.997,385.406z"
                                        ></path>
                                      </g>
                                    </g>
                                    <g>
                                      <g>
                                        <path
                                          d="M255.997,154.725c-55.842,0-101.275,45.433-101.275,101.275s45.433,101.275,101.275,101.275
              s101.275-45.433,101.275-101.275S311.839,154.725,255.997,154.725z M255.997,323.516c-37.23,0-67.516-30.287-67.516-67.516
              s30.287-67.516,67.516-67.516s67.516,30.287,67.516,67.516S293.227,323.516,255.997,323.516z"
                                        ></path>
                                      </g>
                                    </g>
                                  </svg>
                                  <FormattedMessage id="see_details" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="message text-center">
                  <img src={noMessageIcon} alt="" className="no_message" />
                  <p className="noData text-muted text-center h4 mb-0">
                    <FormattedMessage id="no_reservation" />
                  </p>
                  <p className="mb-3">{formatMessage("no_reservations_message")}</p>
                </div>
              )}
            </> :
            <div className="message text-center">
              <img src={noMessageIcon} alt="" className="no_message" />
              <p className="noData text-muted text-center h4 mb-0">
                <FormattedMessage id="no_reservation" />
              </p>
              <p className="mb-3">
                {formatMessage("end_date_must_be_same_or_after_start_date")}
              </p>
            </div>
          }
        </div>
        {(allReservationList?.length > 0 && validateDates(startDate, endDate)) && (
          <div className="row no-gutters p-2 tableFooter">
            <div className="col-6 pt-1">
              <FormattedMessage id="show" />
              <select
                data-testid="pageSelect"
                className="pageLimit"
                onChange={onPageLimitChanged}
                value={pageLimitInfo.pageLimit}
              >
                {pageLimitOptions.map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 d-flex justify-content-end">
              <Pagination
                isRefreshPage={pageInfo.isRefreshPage}
                totalPages={pageInfo.totalPages}
                pageLimit={pageLimitInfo.pageLimit}
                onPageChanged={onPageChanged}
                disablePagination={disablePagination}
                setIsRefreshPage={setIsRefreshPage}
              />
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default observer(ReservationList);
